/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useEffect, useState } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { getNestedVal, isObjEmptyOrNull, getErrorFromEndpoint, isStrEmptyOrNull } from '../UtilityFunctions';
import { useBatchEditable } from './useBatchEditable';
import { useAuthContext } from '../../contexts/AuthContext';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';

interface varInfo {
  digitsOnly?: boolean;
  stripExtraChars?: boolean;
  defaultVal: string | number | boolean;
}

export interface LoanServicingSearchReq {
  loanNumber?: varInfo | string;
  servicerLoanNumber?: varInfo | string;
  ssn?: varInfo | string;
  brwrFirstName?: varInfo | string;
  brwrLastName?: varInfo | string;
  brwrEmail?: varInfo | string;
  propAddress?: varInfo | string;
  propCity?: varInfo | string;
  propState?: varInfo | string;
  propZip?: varInfo | string;
  caseNo?: varInfo | string;
  phoneNo?: varInfo | string;
  monthsSinceCreated?: varInfo | number;
  investorLoanNo?: varInfo | string;
  hazardPolicyNo?: varInfo | string;
  mersId?: varInfo | string;
  taxContractNumber?: varInfo | string;
  bankruptcyCaseNumber?: varInfo | string;
  includeAllBorrowers?: varInfo | boolean;
  fuzzySearch?: varInfo | boolean;
  includeIntlNo?: varInfo | boolean;
  includeScenarioLoans?: varInfo | boolean;
  parcelNo?: varInfo | string;
  miNo?: varInfo | string;
  rcsServicedOnly?: varInfo | boolean;
}

const variableInfo: LoanServicingSearchReq = {
  loanNumber: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  servicerLoanNumber: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  ssn: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  brwrFirstName: { defaultVal: '' },
  brwrLastName: { defaultVal: '' },
  brwrEmail: { defaultVal: '' },
  propAddress: { defaultVal: '' },
  propCity: { defaultVal: '' },
  propState: { digitsOnly: false, stripExtraChars: true, defaultVal: '' },
  propZip: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  caseNo: { defaultVal: '' },
  phoneNo: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  hazardPolicyNo: { defaultVal: '' },
  mersId: { defaultVal: '' },
  taxContractNumber: { defaultVal: '' },
  bankruptcyCaseNumber: { digitsOnly: true, stripExtraChars: true, defaultVal: '' },
  investorLoanNo: { defaultVal: '' },
  includeAllBorrowers: { defaultVal: false },
  fuzzySearch: { defaultVal: false },
  monthsSinceCreated: { defaultVal: 18 },
  includeIntlNo: { defaultVal: false },
  includeScenarioLoans: { defaultVal: false },
  parcelNo: { defaultVal: '' },
  miNo: { defaultVal: '' },
  rcsServicedOnly: { defaultVal: false },
};

function getInitialLoanSearchObj(initialLoanParams: LoanServicingSearchReq = null): LoanServicingSearchReq {
  let finalObj = {};
  for (const key in variableInfo) {
    if (!isObjEmptyOrNull(initialLoanParams) && key in initialLoanParams) {
      finalObj[key] = initialLoanParams[key] == null ? variableInfo[key].defaultVal : initialLoanParams[key];
    } else {
      finalObj[key] = variableInfo[key].defaultVal;
    }
  }
  return finalObj;
}

export function useLoanSearch(initialLoanParams: LoanServicingSearchReq = null) {
  const { token } = useAuthContext();
  const [loans, setLoans] = useState([]);
  const { get } = useAbortController();
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    curr: loanSearchInfo,
    handleChange,
    isChanged,
  } = useBatchEditable<{ [key: string]: any }>({
    initialObj: getInitialLoanSearchObj(initialLoanParams),
  });
  const getFinalVarVal = useCallback(
    (key: string) => {
      let val = getNestedVal(loanSearchInfo, key);
      val = variableInfo[key]?.digitsOnly && typeof val === 'string' ? val.replace(/\D/g, '') : val;
      val = variableInfo[key]?.stripExtraChars && typeof val === 'string' ? val.trim() : val;
      return val;
    },
    [loanSearchInfo],
  );

  const getFinalSearchParams = useCallback(() => {
    let filters = {};
    for (const key in loanSearchInfo) {
      if (isChanged(key) || (!isObjEmptyOrNull(initialLoanParams) && key in initialLoanParams)) {
        let keyVal = getFinalVarVal(key);
        if (
          (typeof keyVal === 'string' && !isStrEmptyOrNull(keyVal)) ||
          typeof keyVal === 'boolean' ||
          typeof keyVal === 'number'
        ) {
          filters[key] = getFinalVarVal(key);
        }
      }
    }
    return filters;
  }, [loanSearchInfo, getFinalVarVal, initialLoanParams, isChanged]);

  const handleError = useCallback((res: Error | AxiosError | any) => {
    const err = getErrorFromEndpoint(res);
    if (isStrEmptyOrNull(err)) return '';
    console.error('Error in searchLoansAPI: ' + err);
    setErrors(err);
    return err;
  }, []);

  const searchLoansAPI = useCallback(
    async (filters: LoanServicingSearchReq) => {
      if (!token) return [];
      if (isObjEmptyOrNull(filters)) {
        setErrors('Fill out one or more of the fields to search');
        return [];
      } else {
        const { loanNumber, ...newFilters } = filters;
        let useNewFilters = false;
        if (loanNumber?.toString().trim().length === 10) {
          newFilters.servicerLoanNumber = loanNumber;
          useNewFilters = true;
        }
        const request = {
          url: process.env.REACT_APP_LOAN_SEARCH,
          data: useNewFilters ? newFilters : filters,
          method: 'post',
        };
        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };
        const config = get();
        const response = await axiosCall(request, config, headers)
          .then((res) => {
            if (isObjEmptyOrNull(res)) return [];
            const err = handleError(res);
            return isStrEmptyOrNull(err) ? res : [];
          })
          .catch((e: AxiosError | Error | any) => {
            handleError(e);
            return [];
          });
        return response as SearchResp[];
      }
    },
    [get, token, handleError],
  );

  const searchLoans = useCallback(async () => {
    setErrors('');
    setLoading(true);
    setLoans([]);
    const filters = getFinalSearchParams();
    const res = await searchLoansAPI(filters)
      .then((res) => {
        setLoans(res);
        return res;
      })
      .finally(() => {
        setLoading(false);
      });
    return res;
  }, [searchLoansAPI, getFinalSearchParams]);

  return {
    loans: loans,
    statuses: {
      loading: loading,
      errors: errors,
    },
    searchLoans: searchLoans,
    loanSearchInfo: loanSearchInfo,
    handleChange: handleChange,
  };
}

export default useLoanSearch;
